/**
 * Session Replay Loader Script - REAL-TIME VERSION
 *
 * This version sends events periodically for better debugging and real-time monitoring.
 * Events are sent as JSON batches - compression happens on the backend.
 *
 * Usage:
 * <script src="https://churnary.com/t.js?id=YOUR_PROJECT_ID" defer></script>
 *
 * Configuration:
 * - id (URL param): Required. Your project ID from the dashboard.
 *
 * Session Management:
 * - Sends events every 15 seconds (configurable)
 * - Ends session after 5 minutes of tab being hidden
 * - Ends session after 30 minutes of user inactivity
 * - Hard cap at 30 minutes maximum session length
 */

import { win, initializeState } from './event-sender/state';
import { redactUrl } from './event-sender/redact';
import { interceptFetch, interceptXHR } from './event-sender/network';
import { loadRRWeb } from './event-sender/recording';
import { installTrackkit } from './event-sender/trackkit';
import { flushPendingChunks } from './event-sender/batch';
import { getOrCreateVisitorId, identify, resendStoredIdentify, setupPassiveCapture } from './event-sender/visitor';

(function () {
  "use strict";

  // Prevent multiple initialization
  if (win.__sessionReplayInitialized) {
    return;
  }
  win.__sessionReplayInitialized = true;

  // Check if recording should be skipped (for internal users)
  const skipRecording = localStorage.getItem('__skipSessionReplay') === 'true';

  // Get configuration from script URL
  const scriptTag = document.currentScript as HTMLScriptElement | null;
  if (!scriptTag) {
    return;
  }

  // Parse project ID from script URL query parameter
  const scriptUrl = new URL(scriptTag.src);
  const projectId = scriptUrl.searchParams.get("id");

  // Backend ingestion endpoint
  // Default: Supabase Edge Function (works from any origin including localhost)
  // Override via ?backend=URL for custom endpoints
  let backendUrl: string;
  const backendOverride = scriptUrl.searchParams.get("backend");
  if (backendOverride) {
    backendUrl = backendOverride;
  } else {
    backendUrl = "https://xdabxhaxoqtqhfkunqjo.supabase.co/functions/v1/ingest-v2";
  }

  const sendInterval = 15000;

  // Validate required configuration
  if (!projectId) {
    return;
  }

  // Generate unique session ID
  let sessionId: string;
  try {
    sessionId = crypto.randomUUID();
  } catch (e) {
    return;
  }

  // Detect mobile device once at initialization
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);

  // Persistent visitor identity (first-party cookie, survives OAuth redirects)
  const visitorId = getOrCreateVisitorId();

  // Initialize shared state with configuration
  initializeState({
    projectId,
    backendUrl,
    sendInterval,
    sessionId,
    isMobile,
    visitorId,
    referrer: redactUrl(document.referrer),
    landingUrl: redactUrl(window.location.href),
  });

  // Expose window.trackkit (custom events + experiment assignment) regardless of whether
  // session recording runs — so product analytics/experiments never depend on rrweb loading.
  installTrackkit();

  // Deliver any final-send chunks a previous page load couldn't get out
  // (keepalive budget rejection, network failure at unload). Runs even when
  // recording is skipped: the queued chunks belong to earlier sessions.
  try {
    flushPendingChunks();
  } catch (e) {
    // Never let the retry flush break tracker init
  }

  // Public API: window.khabib.identify('user_123', { email, name })
  win.khabib = { identify };

  // Re-send a cached identify in case the original beacon was dropped
  resendStoredIdentify();

  // Only start recording if not skipped
  if (!skipRecording) {
    // Passive email capture from in-page form submits (identify() fallback)
    setupPassiveCapture();

    // Intercept network requests early to capture all errors
    interceptFetch();
    interceptXHR();

    // Start loading when DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", loadRRWeb);
    } else {
      // DOM is already ready
      loadRRWeb();
    }
  }


})();
